<template>
  <!--
    Student Journey V2 -- the setup card's button grids. One presentational
    component for the reference's three grid shapes, so their geometry lives
    in one place:

      grade   -- square tiles (`.academic-grade-grid`): 80x80 on desktop, 72x72
                 from the phone tier down
      subject -- 3 columns, 2 from the phone tier down (`.academic-subject-grid`)
      choice  -- 3 columns, 1 from the phone tier down (`.choice-grid--three`)

    Values are measured from the rendered reference (00_*.md §18); the
    breakpoints are the two Borna tiers in `_borna-tokens.scss`, never a local
    literal. It owns no state -- the parent passes the selection in and
    receives `select`.

    SX-03B (doc 22 §10, DESIGN-20): an option may be `unavailable` (a course
    without AI Tutor availability). It stays visible and keyboard/touch
    reachable -- a real button with `aria-disabled`, never the `disabled`
    attribute -- carries the «به‌زودی» badge, can never be selected, and its
    activation emits `unavailable` (the parent opens the shared Coming Soon
    dialog) instead of `select`.
  -->
  <div class="setup-choice-grid" :class="`setup-choice-grid--${variant}`">
    <button
      v-for="option in options"
      :key="option.value"
      type="button"
      class="setup-choice-grid__option"
      :class="{
        'setup-choice-grid__option--selected':
          !option.unavailable && option.value === value,
        'setup-choice-grid__option--unavailable': option.unavailable,
      }"
      :aria-pressed="
        option.unavailable ? null : option.value === value ? 'true' : 'false'
      "
      :aria-disabled="option.unavailable ? 'true' : null"
      :data-testid="`${testidPrefix}-${option.value}`"
      :data-unavailable="option.unavailable ? 'true' : null"
      @click="onOptionClick(option)"
    >
      <span class="setup-choice-grid__label">{{ option.label }}</span>
      <span
        v-if="option.unavailable"
        class="setup-choice-grid__badge"
        :data-testid="`${testidPrefix}-${option.value}-coming-soon`"
        >{{ unavailableLabel }}</span
      >
    </button>
  </div>
</template>

<script>
export default {
  name: 'SetupChoiceGrid',
  props: {
    // [{ value: string, label: string, unavailable?: boolean }]
    options: { type: Array, required: true },
    value: { type: String, default: '' },
    variant: {
      type: String,
      default: 'choice',
      validator: v => ['grade', 'subject', 'choice'].includes(v),
    },
    testidPrefix: { type: String, default: 'ai-tutor-option' },
    // The badge text for unavailable options (the parent resolves the copy).
    unavailableLabel: { type: String, default: '' },
  },
  methods: {
    onOptionClick(option) {
      // Enter / Space on a real <button> arrive here as a click too.
      if (option.unavailable) {
        this.$emit('unavailable', option.value)
        return
      }
      this.$emit('select', option.value)
    },
  },
}
</script>

<style lang="scss" scoped>
.setup-choice-grid {
  display: grid;
  gap: 8px;
}

.setup-choice-grid__option {
  box-sizing: border-box;
  min-width: 0;
  padding: 0;
  border: 1px solid $borna-border;
  border-radius: 13px;
  background: $borna-surface;
  color: $borna-text;
  font-size: $borna-fs-nav;
  font-weight: $borna-fw-regular;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    border-color: $borna-indigo-300;
  }

  &--selected,
  &--selected:hover {
    border-color: $borna-indigo-600;
    background: $borna-indigo-50;
    color: $borna-indigo-900;
    font-weight: $borna-fw-extrabold;
  }

  // SX-03B: the Reports V2 disabled-pill vocabulary (dashed border, canvas
  // fill, muted text) -- muted, never invisible, still a real target. The
  // muted text keeps >= 4.5:1 contrast on the canvas fill.
  &--unavailable,
  &--unavailable:hover {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 4px 6px;
    padding: 4px 8px;
    border-style: dashed;
    background: $borna-canvas;
    color: $borna-muted;
    font-weight: $borna-fw-regular;
  }

  &--unavailable:hover,
  &--unavailable:focus-visible {
    border-color: $borna-indigo-300;
  }
}

// The same «به‌زودی» pill the Home cards use (HomeActionCard.vue), sized for
// a 45px tile.
.setup-choice-grid__badge {
  padding: 1px 7px;
  border-radius: 999px;
  background: #fff7ed;
  color: #9a5b13;
  font-size: 10px;
  font-weight: $borna-fw-bold;
  line-height: 1.6;
  white-space: nowrap;
}

// ---- grade: square tiles ---------------------------------------------------
.setup-choice-grid--grade {
  grid-template-columns: repeat(auto-fit, minmax(68px, 80px));
  justify-content: start;

  .setup-choice-grid__option {
    aspect-ratio: 1;
    padding: 0;
    border-radius: 15px;
  }

  .setup-choice-grid__option--selected {
    box-shadow: inset 0 0 0 1px rgba(49, 75, 214, 0.08);
  }

  @include borna-phone {
    grid-template-columns: repeat(3, 72px);

    .setup-choice-grid__option {
      width: 72px;
      border-radius: 14px;
    }
  }
}

// ---- subject: 3 -> 2 columns ----------------------------------------------
.setup-choice-grid--subject {
  grid-template-columns: repeat(3, minmax(0, 1fr));

  .setup-choice-grid__option {
    min-height: 45px;
  }

  .setup-choice-grid__option--selected {
    box-shadow: inset 0 0 0 1px rgba(49, 75, 214, 0.08);
  }

  @include borna-phone {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

// ---- choice: 3 -> 1 columns -----------------------------------------------
.setup-choice-grid--choice {
  grid-template-columns: repeat(3, minmax(0, 1fr));

  .setup-choice-grid__option {
    min-height: 48px;
  }

  @include borna-phone {
    grid-template-columns: 1fr;
  }
}
</style>
